import { Box, Chip, FormControlLabel, Switch, TextField } from '@mui/material'
import { FunctionComponent, useEffect, useState } from 'react'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import { CompanyHousePicker } from '../../presentation-components/form-field-components/company-house-picker'
import SuperAdminService from '../../../shared/services/super-admin/super-admin.service'
import { ICompanyHouseSearchResult, ISupplierMatchOverride } from '../../../shared/services/super-admin/super-admin.model'

export const AddEditSupplierMatchDialog: FunctionComponent<{
  open: boolean,
  onClose: () => void,
  override?: ISupplierMatchOverride | null,
  onSaved: () => void,
}> = ({ open, onClose, override, onSaved }) => {
  const superAdminService = new SuperAdminService()

  const [alias, setAlias] = useState('')
  const [notes, setNotes] = useState('')
  const [active, setActive] = useState(true)
  const [selectedCompany, setSelectedCompany] = useState<ICompanyHouseSearchResult | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setAlias(override?.alias ?? '')
    setNotes(override?.notes ?? '')
    setActive(override?.active ?? true)
    setSelectedCompany(override && override.companyNumber
      ? { companyName: override.companyName ?? '', companyNumber: override.companyNumber, sicCode: override.sicCode, companyStatus: null }
      : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, override])

  const submitHandler = () => {
    if (!alias.trim() || !selectedCompany) return
    const payload = {
      alias: alias.trim(),
      companyNumber: selectedCompany.companyNumber,
      companyName: selectedCompany.companyName,
      sicCode: selectedCompany.sicCode ?? null,
      notes: notes.trim() || null,
      active,
    }
    setIsSubmitting(true)
    const request = override
      ? superAdminService.updateSupplierMatchOverride(override.id, payload)
      : superAdminService.createSupplierMatchOverride(payload)
    request
      .then(() => { onSaved(); onClose() })
      .catch(() => { /* handled upstream */ })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={override ? 'Edit supplier match' : 'Add supplier match'}
      cancelHandler={onClose}
      submitHandler={submitHandler}
      isSubmitting={isSubmitting}
      disableSaveButton={!alias.trim() || !selectedCompany}
      maxWidth="sm"
      submitButtonText="Save"
      submitButtonLoadingText="Saving"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <TextField
          label="Alias (what a customer types)"
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          size="small"
          fullWidth
          helperText="e.g. Amazon. Add each variant you want to catch as a separate entry."
        />

        <CompanyHousePicker
          value={selectedCompany}
          onChange={setSelectedCompany}
          search={(term) => superAdminService.searchCompanyHouse(term)}
        />

        {selectedCompany &&
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`No. ${selectedCompany.companyNumber}`} />
            {selectedCompany.sicCode && <Chip size="small" label={`SIC ${selectedCompany.sicCode}`} />}
          </Box>}

        <TextField
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          size="small"
          fullWidth
        />

        <FormControlLabel
          control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
          label="Active"
        />
      </Box>
    </DialogWrapper>
  )
}
